import React, { useState } from 'react';
import ceoAdPdf from './CEO_Advertisement.pdf';
import {
  Briefcase,
  Heart,
  Globe,
  Zap,
  Users,
  Sparkles,
  ArrowRight,
  MapPin,
  Calendar,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Award,
  Clock,
  Building2,
  FileText,
  CheckCircle2,
  ShieldCheck,
  History,
  User,
  FileSignature
} from 'lucide-react';

export default function App() {
  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      <main>
        <HeroSection />
        <JobOpenings />
      </main>
    </div>
  );
}

// --- Components ---

function HeroSection() {
  return (
    <section className="relative pt-24 pb-32 overflow-hidden bg-slate-50">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-white" />
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 rounded-full bg-blue-100/50 blur-3xl opacity-70" />
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 rounded-full bg-indigo-100/50 blur-3xl opacity-70" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <span className="inline-block py-1.5 px-4 rounded-full bg-blue-100 text-blue-800 font-semibold text-sm mb-6 border border-blue-200 shadow-sm">
          We are Hiring
        </span>
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tight text-slate-900 mb-6 leading-tight">
          Shape the future of <br className="hidden md:block" />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-indigo-600">
            India's startup ecosystem.
          </span>
        </h1>
        <p className="mt-4 max-w-2xl mx-auto text-lg md:text-xl text-slate-600 mb-10">
          Join the Technology Business Incubator Foundation at IIT Ropar.
          Help us nurture technology-oriented and engineering startups with high growth potential.
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <button onClick={() => document.getElementById('roles').scrollIntoView({ behavior: 'smooth' })} className="flex items-center justify-center gap-2 bg-blue-700 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-blue-800 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5">
            View Open Roles
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
}

function JobOpenings() {
  const [isExpanded, setIsExpanded] = useState(true);

  const jobDetails = {
    title: "Chief Executive Officer (CEO)",
    organization: "IIT Ropar - Technology Business Incubator Foundation",
    location: "IIT Ropar, Rupnagar, Punjab",
    type: "Full-Time",
    deadline: "6th October 2026", // UPDATED from PDF
    applyLink: "https://forms.gle/ReDo7YGzj3QsA744A",
    documentLink: ceoAdPdf,
    about: "IIT Ropar - Technology Business Incubator Foundation, a Section 8 company established under the Department of Science and Technology (DST), Government of India at the Indian Institute of Technology Ropar (IIT Ropar), aims to strengthen the startup ecosystem locally and nationally. We are seeking applications for the position of Chief Executive Officer (CEO) responsible for leading the TBIF's operations and positioning the IIT Ropar as a prominent leader in nurturing technology-oriented and engineering startups.",
    description: [
      "Manage the incubator's daily operations spanning start-ups, MSMEs, academic collaborations, CSR and other activities with support of the incubator's team",
      "Raise and manage capital to ensure financial sustainability and growth of IIT Ropar-TBIF",
      "Work relentlessly to attract quality startups with high growth potential to IIT Ropar - TBIF",
      "Scale-up and manage IIT Ropar-TBIF's start-up investment portfolio",
      "Hire quality talent to accelerate the human resource capabilities of the incubator",
      "Formulate incubator policies that synergize with IIT Ropar's strategic interests",
      "Seek and materialize strategic relationships with national and international funding agencies, investors, CSR donors and internal or external stakeholders",
      "Define, organize and disseminate accurate and timely reports to the Board of Directors (BoD), funding agencies, and all other stakeholders",
      "Conduct annual events for promotion of start-ups",
      "Any other items deemed to be of significance by the Directors of the organization"
    ],
    skills: [
      "Strong team building and people-management skills, with the ability to set priorities and manage operations",
      "Out-of-the-box thinker with a technical bent of mind, capable of interacting with faculty and students alike",
      "Financially prudent with an understanding of P&L / Balance Sheet",
      "Excellent writing, presentation, communication, negotiation and networking skills",
      "Detailed knowledge of business incubators and accelerators",
      "Know-how on startup fundraising stages and equity sharing arrangements",
      "Knowledge of company law and compliances",
      "Good grasp over fundamentals of sales & marketing",
      "Experience with writing funding applications is desirable"
    ],
    requirements: [
      // UPDATED Age Requirement from PDF (< 45 Years)
      { label: "Age Requirement", value: "< 45 Years, preferred." },
      // UPDATED Experience from PDF (> 10+ years)
      { label: "Professional Experience", value: "> 10+ years of work experience with > 5 years in an administrative/managerial role (preferably). Candidates with an exceptional track record may be considered." },
      { label: "Remuneration & Contract", value: "To be determined based on qualifications and performance, respectively." }
    ]
  };

  return (
    <section id="roles" className="py-24 bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-end mb-10 gap-4">
          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Open Positions</h2>
            <p className="text-slate-600">Join our leadership team and make a lasting impact.</p>
          </div>
          {/* UPDATED: Changed from 'Closed' to active status since deadline is Oct 6, 2026 */}
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 shadow-sm">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
            </span>
            Accepting Applications
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden transition-all duration-300">
          <div
            className="p-6 sm:p-8 cursor-pointer hover:bg-slate-50 transition-colors flex flex-col md:flex-row justify-between md:items-center gap-6"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="bg-blue-50 border border-blue-200 text-blue-800 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">Leadership</span>
                <span className="text-slate-600 text-sm font-medium flex items-center gap-1.5">
                  <Clock className="w-4 h-4" /> {jobDetails.type}
                </span>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">{jobDetails.title}</h3>
              <div className="flex flex-wrap items-center gap-5 text-sm text-slate-600">
                <div className="flex items-center gap-2 font-medium">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  {jobDetails.organization}
                </div>
                <div className="flex items-center gap-2 font-medium">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  {jobDetails.location}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between md:justify-end gap-6 w-full md:w-auto">
              <div className="text-left md:text-right">
                <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-1">Apply By</p>
                <p className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
                  <Calendar className="w-4 h-4 text-rose-500" /> {jobDetails.deadline}
                </p>
              </div>
              <button className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors">
                {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {isExpanded && (
            <div className="p-6 sm:p-8 border-t border-slate-200 bg-white">
              <div className="max-w-none">

                {/* About Section */}
                <div className="mb-10">
                  <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4 border-b border-slate-100 pb-2">
                    About the Role
                  </h4>
                  <p className="text-slate-700 leading-relaxed text-sm sm:text-base">
                    {jobDetails.about}
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-12 mb-10">
                  {/* Job Description */}
                  <div>
                    <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4 border-b border-slate-100 pb-2 flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-slate-500" />
                      Key Responsibilities
                    </h4>
                    <ul className="space-y-3.5">
                      {jobDetails.description.map((item, idx) => (
                        <li key={idx} className="flex gap-3 text-slate-700 text-sm">
                          <div className="mt-1.5 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-blue-600" />
                          <span className="leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Skills Required */}
                  <div>
                    <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4 border-b border-slate-100 pb-2 flex items-center gap-2">
                      <Zap className="w-4 h-4 text-slate-500" />
                      Required Skills & Expertise
                    </h4>
                    <ul className="space-y-3.5">
                      {jobDetails.skills.map((item, idx) => (
                        <li key={idx} className="flex gap-3 text-slate-700 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                          <span className="leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="bg-slate-50/80 p-6 sm:p-8 rounded-2xl border border-slate-200 mb-10">
                  <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-6 flex items-center gap-2 border-b border-slate-200 pb-4">
                    <ShieldCheck className="w-5 h-5 text-blue-600" />
                    Eligibility & Additional Details
                  </h4>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Professional Experience Card */}
                    <div className="md:col-span-2 bg-white border border-slate-200 p-5 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-2 mb-3">
                        <History className="w-4 h-4 text-indigo-500" />
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Professional Experience</span>
                      </div>
                      <p className="text-sm text-slate-800 leading-relaxed pl-6">
                        <strong>&gt; 10+ years</strong> of work experience with <strong>&gt; 5 years</strong> in an administrative/managerial role (preferably). Candidates with an exceptional track record may be considered.
                      </p>
                    </div>

                    {/* Age Requirement Card */}
                    <div className="bg-white border border-slate-200 p-5 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-2 mb-3">
                        <User className="w-4 h-4 text-emerald-500" />
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Age Requirement</span>
                      </div>
                      <p className="text-sm text-slate-800 font-medium leading-relaxed pl-6">
                        &lt; 45 Years, preferred.
                      </p>
                    </div>

                    {/* Remuneration Card */}
                    <div className="bg-white border border-slate-200 p-5 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-2 mb-3">
                        <FileSignature className="w-4 h-4 text-amber-500" />
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Remuneration & Contract</span>
                      </div>
                      <p className="text-sm text-slate-800 font-medium leading-relaxed pl-6">
                        To be determined based on qualifications and performance, respectively.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-between gap-6 pt-8 border-t border-slate-200">
                  <div className="max-w-xl">
                    <p className="text-sm text-slate-600">
                      <strong className="text-slate-900">Please Note:</strong> Only shortlisted candidates shall be called for the further recruitment process. The decision of the management for the shortlisting and selection will be final.
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row w-full sm:w-auto gap-4">
                    <a
                      href={jobDetails.documentLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full sm:w-auto flex items-center justify-center gap-2 bg-white text-slate-700 border border-slate-300 px-6 py-3 rounded-xl font-bold hover:bg-slate-50 hover:text-blue-700 transition-all whitespace-nowrap"
                    >
                      <FileText className="w-4 h-4" />
                      View Advertisement
                    </a>
                    <a
                      href={jobDetails.applyLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full sm:w-auto flex items-center justify-center gap-2 bg-blue-700 text-white px-8 py-3 rounded-xl font-bold hover:bg-blue-800 transition-all shadow-md hover:shadow-lg whitespace-nowrap"
                    >
                      Apply Now
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>

              </div>
            </div>
          )}
        </div>

      </div>
    </section>
  );
}